import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

type LoginFormProps = {
  onSubmit: (code: string) => void;
  error: string | null;
  submitting: boolean;
  onForgot: () => void;
};

export default function LoginForm({ onSubmit, error, submitting, onForgot }: LoginFormProps) {
  const { t } = useTranslation();
  const [code, setCode] = useState('');

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onSubmit(code);
  };

  return (
    <div className="w-full max-w-md animate-fade-rise">
      <span className="inline-flex items-center gap-2 rounded-full bg-secondary-100 px-3 py-1.5 text-xs font-semibold text-secondary-900">
        <i className="ri-shield-keyhole-line text-sm"></i>
        {t('login.badge')}
      </span>

      <h1 className="mt-5 font-heading text-3xl font-semibold leading-tight text-foreground-950">
        {t('login.title')}
      </h1>
      <p className="mt-3 text-sm leading-relaxed text-foreground-600">{t('login.subtitle')}</p>

      <form onSubmit={handleSubmit} className="mt-8 space-y-5">
        <div>
          <label
            htmlFor="access-code"
            className="mb-2 block text-sm font-semibold text-foreground-800"
          >
            {t('login.codeLabel')}
          </label>
          <div className="relative">
            <span className="pointer-events-none absolute left-4 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center text-foreground-400">
              <i className="ri-fingerprint-line text-lg"></i>
            </span>
            <input
              id="access-code"
              name="access_code"
              type="text"
              inputMode="numeric"
              autoComplete="off"
              value={code}
              onChange={(event) => setCode(event.target.value)}
              placeholder={t('login.codePlaceholder')}
              className="w-full rounded-md border border-background-300 bg-background-50 py-3 pl-12 pr-4 text-base tracking-wide text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-200"
            />
          </div>
          <p className="mt-2 flex items-start gap-2 text-xs text-foreground-500">
            <i className="ri-information-line mt-0.5"></i>
            {t('login.helper')}
          </p>
        </div>

        {error && (
          <div className="flex items-start gap-2 rounded-md border border-secondary-300 bg-secondary-50 px-4 py-3 text-sm text-secondary-900">
            <i className="ri-error-warning-line mt-0.5 text-base"></i>
            <span>{error}</span>
          </div>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-70"
        >
          {submitting ? (
            <i className="ri-loader-4-line animate-spin text-lg"></i>
          ) : (
            <i className="ri-login-box-line text-lg"></i>
          )}
          {submitting ? t('login.submitting') : t('login.submit')}
        </button>
      </form>

      <div className="mt-6 flex items-center justify-center">
        <button
          type="button"
          onClick={onForgot}
          className="cursor-pointer text-sm font-semibold text-primary-700 underline-offset-4 hover:underline"
        >
          {t('login.forgot')}
        </button>
      </div>
    </div>
  );
}